import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { gardenAPI } from '../lib/api';
import { 
  Leaf, Loader2, CheckCircle, XCircle, Clock, 
  TrendingUp, Users, DollarSign, RefreshCw,
  Eye, Edit2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function AdminGardenPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [domainsRes, statsRes] = await Promise.all([
        gardenAPI.adminList({ status: filter }),
        gardenAPI.adminStats(),
      ]);
      setDomains(domainsRes.data.domains || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load garden data');
    }
    setLoading(false);
  };

  const handleUpdate = async (id) => {
    try {
      await gardenAPI.adminUpdate(id, editForm);
      toast.success('Domain updated');
      setEditingId(null);
      loadData();
    } catch {
      toast.error('Update failed');
    }
  };

  const startEdit = (domain) => {
    setEditingId(domain.id);
    setEditForm({
      status: domain.status,
      authority_score: domain.authority_score || 0,
      traffic_monthly: domain.traffic_monthly || 0,
      estimated_value: domain.estimated_value || 0,
      admin_notes: domain.admin_notes || '',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500/20 text-amber-400',
      accepted: 'bg-blue-500/20 text-blue-400',
      active: 'bg-green-500/20 text-green-400',
      reclaimed: 'bg-purple-500/20 text-purple-400',
      expired: 'bg-red-500/20 text-red-400',
      reclaim_requested: 'bg-cyan-500/20 text-cyan-400',
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00FF94]" />
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-8" data-testid="admin-garden-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Outfit'] flex items-center gap-3">
            <Leaf className="h-8 w-8 text-[#00FF94]" />
            Domain Garden Management
          </h1>
          <p className="text-zinc-500 mt-1">Manage domains in the garden program</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" className="border-zinc-700 text-zinc-300">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Total</p>
            <p className="text-2xl font-bold text-white">{stats?.total || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Pending</p>
            <p className="text-2xl font-bold text-amber-400">{stats?.pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Active</p>
            <p className="text-2xl font-bold text-green-400">{stats?.active || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Reclaimed</p>
            <p className="text-2xl font-bold text-purple-400">{stats?.reclaimed || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Expiring Soon</p>
            <p className="text-2xl font-bold text-red-400">{stats?.nearing_expiry || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <p className="text-zinc-500 text-xs">Total Value</p>
            <p className="text-2xl font-bold text-[#00E5FF]">${stats?.total_estimated_value?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="mb-6 flex gap-2">
        {['', 'pending', 'active', 'reclaim_requested', 'expired'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilter(status); }}
            className={filter === status ? 'bg-[#00FF94] text-black' : 'border-zinc-700 text-zinc-300'}
          >
            {status || 'All'}
          </Button>
        ))}
      </div>

      {/* Domains Table */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Domain</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Authority</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Traffic</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Value</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Care Period</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain) => (
                  <React.Fragment key={domain.id}>
                    <tr className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{domain.domain_name}</p>
                        <p className="text-zinc-500 text-xs">{domain.contact_email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(domain.status)}`}>
                          {domain.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-zinc-300">{domain.authority_score || '-'}</td>
                      <td className="py-3 px-4 text-zinc-300">{domain.traffic_monthly?.toLocaleString() || '-'}</td>
                      <td className="py-3 px-4 text-green-400">${domain.estimated_value?.toLocaleString() || 0}</td>
                      <td className="py-3 px-4 text-zinc-400 text-sm">
                        {domain.care_start_date ? (
                          <>
                            {domain.care_start_date.split('T')[0]} -<br />
                            {domain.care_end_date?.split('T')[0]}
                          </>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => startEdit(domain)}
                          className="text-[#00E5FF]"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                    {editingId === domain.id && (
                      <tr className="bg-zinc-900/50">
                        <td colSpan={7} className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <Label className="text-zinc-400 text-xs">Status</Label>
                              <select
                                value={editForm.status}
                                onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
                                className="w-full mt-1 h-10 bg-zinc-800 border border-zinc-700 rounded text-white px-3"
                              >
                                <option value="pending">Pending</option>
                                <option value="accepted">Accepted</option>
                                <option value="active">Active</option>
                                <option value="reclaimed">Reclaimed</option>
                                <option value="expired">Expired</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Authority Score (0-100)</Label>
                              <Input
                                type="number"
                                value={editForm.authority_score}
                                onChange={(e) => setEditForm(p => ({ ...p, authority_score: parseInt(e.target.value) || 0 }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Monthly Traffic</Label>
                              <Input
                                type="number"
                                value={editForm.traffic_monthly}
                                onChange={(e) => setEditForm(p => ({ ...p, traffic_monthly: parseInt(e.target.value) || 0 }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Estimated Value ($)</Label>
                              <Input
                                type="number"
                                value={editForm.estimated_value}
                                onChange={(e) => setEditForm(p => ({ ...p, estimated_value: parseInt(e.target.value) || 0 }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                          </div>
                          <div className="mb-4">
                            <Label className="text-zinc-400 text-xs">Admin Notes</Label>
                            <Textarea
                              value={editForm.admin_notes}
                              onChange={(e) => setEditForm(p => ({ ...p, admin_notes: e.target.value }))}
                              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleUpdate(domain.id)} className="bg-[#00FF94] text-black hover:bg-[#00FF94]/80">
                              Save Changes
                            </Button>
                            <Button variant="outline" onClick={() => setEditingId(null)} className="border-zinc-700 text-zinc-300">
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {domains.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-zinc-500">
                      No domains in the garden program yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
